import { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Upload, FileText, X, CheckCircle, AlertCircle, AlertTriangle, Loader, Loader2, Trash2, RefreshCw, Search, Eye, Table2 } from "lucide-react";
import { VaultProvenanceBadge } from "../components/VaultProvenanceBadge";
import { useTaxYear } from "../contexts/TaxYearContext";
import { useAuth } from "../contexts/AuthContext";
import ArchiveButton from "../components/ArchiveButton";
import SpreadsheetMappingPanel from "../components/SpreadsheetMappingPanel";
import { DOCUMENT_TYPES } from "../constants/documentTypes";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from "../components/ui/alert-dialog";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Zone type names are bound to the shared DOCUMENT_TYPES constant (by index,
// same order as backend's DOCUMENT_TYPES in server.py) so this list and the
// Vault "Process in Tax App" modal can never drift apart.
const uploadZones = [
  {
    type: DOCUMENT_TYPES[0],
    icon: "🏦",
    iconColor: "text-blue-600",
    bgColor: "bg-blue-50",
    borderColor: "border-blue-200 hover:border-blue-400",
    desc: "Checking, savings, business bank statements",
    accept: ".pdf,.csv,.xls,.xlsx,.qbo,.ofx"
  },
  {
    type: DOCUMENT_TYPES[1],
    icon: "📋",
    iconColor: "text-purple-600",
    bgColor: "bg-purple-50",
    borderColor: "border-purple-200 hover:border-purple-400",
    desc: "CACFP monthly claim reports and summaries",
    accept: ".pdf,.csv,.xlsx"
  },
  {
    type: DOCUMENT_TYPES[2],
    icon: "📄",
    iconColor: "text-emerald-600",
    bgColor: "bg-emerald-50",
    borderColor: "border-emerald-200 hover:border-emerald-400",
    desc: "Subsidy reports, 1099-NEC, 1099-MISC documents",
    accept: ".pdf,.csv,.xlsx"
  },
  {
    type: DOCUMENT_TYPES[3],
    icon: "⚡",
    iconColor: "text-amber-600",
    bgColor: "bg-amber-50",
    borderColor: "border-amber-200 hover:border-amber-400",
    desc: "Electric, gas, water utility bills",
    accept: ".pdf,.png,.jpg,.jpeg"
  },
  {
    type: DOCUMENT_TYPES[4],
    icon: "🧾",
    iconColor: "text-rose-600",
    bgColor: "bg-rose-50",
    borderColor: "border-rose-200 hover:border-rose-400",
    desc: "Receipts, invoices, field trips, curriculum, supplies, purchase records",
    accept: ".pdf,.png,.jpg,.jpeg"
  },
  {
    type: DOCUMENT_TYPES[5],
    icon: "👥",
    iconColor: "text-teal-600",
    bgColor: "bg-teal-50",
    borderColor: "border-teal-200 hover:border-teal-400",
    desc: "ADP payroll reports, W-2 info, staff records",
    accept: ".pdf,.csv,.xlsx"
  },
  {
    type: DOCUMENT_TYPES[6],
    icon: "❤️",
    iconColor: "text-pink-600",
    bgColor: "bg-pink-50",
    borderColor: "border-pink-200 hover:border-pink-400",
    desc: "Aetna, BCBS, dental, vision, medical premiums",
    accept: ".pdf,.png,.jpg,.jpeg,.csv,.xlsx"
  },
  {
    type: DOCUMENT_TYPES[7],
    icon: "🚗",
    iconColor: "text-indigo-600",
    bgColor: "bg-indigo-50",
    borderColor: "border-indigo-200 hover:border-indigo-400",
    desc: "Vehicle policy premiums, commercial auto",
    accept: ".pdf,.png,.jpg,.jpeg,.csv,.xlsx"
  },
  {
    type: DOCUMENT_TYPES[8],
    icon: "🏢",
    iconColor: "text-sky-600",
    bgColor: "bg-sky-50",
    borderColor: "border-sky-200 hover:border-sky-400",
    desc: "Building, liability, workers comp, property",
    accept: ".pdf,.png,.jpg,.jpeg,.csv,.xlsx"
  },
  {
    type: DOCUMENT_TYPES[9],
    icon: "🏛️",
    iconColor: "text-stone-600",
    bgColor: "bg-stone-50",
    borderColor: "border-stone-200 hover:border-stone-400",
    desc: "County, city, school district property tax bills",
    accept: ".pdf,.png,.jpg,.jpeg,.csv,.xlsx"
  },
  {
    type: DOCUMENT_TYPES[10],
    icon: "🔧",
    iconColor: "text-slate-600",
    bgColor: "bg-slate-50",
    borderColor: "border-slate-200 hover:border-slate-400",
    desc: "Building repairs, equipment service, cleaning",
    accept: ".pdf,.png,.jpg,.jpeg,.csv,.xlsx"
  },
  {
    type: DOCUMENT_TYPES[11],
    icon: "💰",
    iconColor: "text-green-600",
    bgColor: "bg-green-50",
    borderColor: "border-green-200 hover:border-green-400",
    desc: "Private-pay co-payments, late fees, enrollment fees",
    accept: ".pdf,.png,.jpg,.jpeg,.csv,.xlsx"
  },
  {
    type: DOCUMENT_TYPES[12],
    icon: "🍎",
    iconColor: "text-lime-600",
    bgColor: "bg-lime-50",
    borderColor: "border-lime-200 hover:border-lime-400",
    desc: "Sam's Club, Costco, grocery food receipts",
    accept: ".pdf,.png,.jpg,.jpeg,.csv,.xlsx"
  },
  {
    type: DOCUMENT_TYPES[13],
    icon: "📞",
    iconColor: "text-cyan-600",
    bgColor: "bg-cyan-50",
    borderColor: "border-cyan-200 hover:border-cyan-400",
    desc: "Cell phone, business landline, internet service",
    accept: ".pdf,.png,.jpg,.jpeg,.csv,.xlsx"
  },
  {
    type: DOCUMENT_TYPES[14],
    icon: "💳",
    iconColor: "text-violet-600",
    bgColor: "bg-violet-50",
    borderColor: "border-violet-200 hover:border-violet-400",
    desc: "Visa, Mastercard, Amex, business credit card statements",
    accept: ".pdf,.csv,.xlsx"
  },
  {
    type: DOCUMENT_TYPES[15],
    icon: "🚙",
    iconColor: "text-amber-700",
    bgColor: "bg-amber-50",
    borderColor: "border-amber-200 hover:border-amber-400",
    desc: "Fuel, oil, tires, repairs, registration — for actual-expense IRS method",
    accept: ".pdf,.png,.jpg,.jpeg,.csv,.xlsx"
  },
  {
    type: DOCUMENT_TYPES[16],
    icon: "✏️",
    iconColor: "text-orange-600",
    bgColor: "bg-orange-50",
    borderColor: "border-orange-200 hover:border-orange-400",
    desc: "Cash payments, missed items, anything OCR couldn't read — type rows by hand",
    accept: "manual",
    manual: true
  },
  {
    type: DOCUMENT_TYPES[17],
    icon: "📁",
    iconColor: "text-gray-600",
    bgColor: "bg-gray-50",
    borderColor: "border-gray-200 hover:border-gray-400",
    desc: "Examples: loans, license renewals, trainings, anything else",
    accept: ".pdf,.png,.jpg,.jpeg,.csv,.xlsx"
  },
  {
    type: DOCUMENT_TYPES[18],
    icon: "🏅",
    iconColor: "text-emerald-600",
    bgColor: "bg-emerald-50",
    borderColor: "border-emerald-200 hover:border-emerald-400",
    desc: "Award letters, disbursement notices, grant statements — flows to Grant Tracking",
    accept: ".pdf,.png,.jpg,.jpeg,.csv,.xlsx"
  },
  {
    type: DOCUMENT_TYPES[19],
    icon: "🖥️",
    iconColor: "text-blue-600",
    bgColor: "bg-blue-50",
    borderColor: "border-blue-200 hover:border-blue-400",
    desc: "Purchase receipts and invoices for equipment — flows to Equipment & Assets",
    accept: ".pdf,.png,.jpg,.jpeg,.csv,.xlsx"
  }
];

const statusIcon = { processed: CheckCircle, pending: Loader, processing: Loader, error: AlertCircle };
const statusClr = { processed: "text-emerald-500", pending: "text-amber-500", processing: "text-blue-500", error: "text-red-500" };
const fmt = n => (n / 1024).toFixed(1) + " KB";

export default function UploadExtract() {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState({});
  const [dragOver, setDragOver] = useState(null);
  const [error, setError] = useState("");
  const [historySearch, setHistorySearch] = useState("");
  const [historyTypeFilter, setHistoryTypeFilter] = useState("");
  const [historyStatusFilter, setHistoryStatusFilter] = useState("");
  const [selectedDocs, setSelectedDocs] = useState({});
  const [printing, setPrinting] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // { kind: "single"|"bulk", ids: [...], name?, txnCount }
  const [deleting, setDeleting] = useState(false);
  // Spreadsheet Import Pipeline — list of docs (csv/xls/xlsx) awaiting user
  // confirmation of column mapping. One panel renders per pending doc.
  const [pendingSpreadsheets, setPendingSpreadsheets] = useState([]);
  // Deep-link highlight — Vault's "Processed — Tax Year" link lands here with
  // ?focus_doc=<id> to point at the specific processed document.
  const [highlightDocId, setHighlightDocId] = useState(null);
  const handledFocusDocRef = useRef(null); // last focus_doc already scrolled/highlighted
  const rowRefs = useRef({});
  const mappingPanelRef = useRef(null);
  const [scrollToMappingKey, setScrollToMappingKey] = useState(null);
  const [searchParams] = useSearchParams();
  const { taxYear } = useTaxYear();
  const { user } = useAuth();

  // Ship Gate 3a — per-card "Print Report" PDF download.
  const handlePrintCard = async (docType) => {
    setPrinting(docType);
    try {
      const url = `${API}/reports/card-pdf?document_type=${encodeURIComponent(docType)}&tax_year=${taxYear}`;
      const { data } = await axios.get(url, { responseType: "blob", withCredentials: true });
      const blobUrl = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      const center = (user?.center_name || "center").toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "").slice(0, 40);
      const card = docType.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
      a.href = blobUrl;
      a.download = `${card}-${center}-${taxYear}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success(`${docType} report ready`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not generate report");
    } finally {
      setPrinting(null);
    }
  };
  const fileRefs = useRef({});
  const navigate = useNavigate();

  const fetchDocs = async () => {
    try {
      const { data } = await axios.get(`${API}/documents`, { withCredentials: true });
      setDocs(data);
    } catch {}
  };

  useEffect(() => {
    fetchDocs();
    const interval = setInterval(fetchDocs, 8000);
    return () => clearInterval(interval);
  }, []);

  // Deep-link from Vault's "Processed — Tax Year" button (?focus_doc=<id>) —
  // scroll the specific document's History row into view and briefly highlight
  // it ONCE. fetchDocs() re-runs every 8s and produces a new `docs` array
  // reference each time, so a ref (not state) tracks the last focus_doc we've
  // already handled — prevents re-scrolling/re-highlighting on every poll.
  useEffect(() => {
    const focusDocId = searchParams.get("focus_doc");
    if (!focusDocId || docs.length === 0) return;
    if (handledFocusDocRef.current === focusDocId) return;
    const el = rowRefs.current[focusDocId];
    if (!el) return;
    handledFocusDocRef.current = focusDocId;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightDocId(focusDocId);
    const t = setTimeout(() => setHighlightDocId(null), 3000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docs, searchParams]);

  // Scrolls the mapping panel into view AFTER pendingSpreadsheets has
  // actually re-rendered (not via requestAnimationFrame inside the click
  // handler, which can race the state-update commit). scrollToMappingKey is
  // only set when openMapping added a genuinely NEW entry.
  useEffect(() => {
    if (!scrollToMappingKey) return;
    if (!pendingSpreadsheets.some(p => p.uploadKey === scrollToMappingKey)) return;
    mappingPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    setScrollToMappingKey(null);
  }, [pendingSpreadsheets, scrollToMappingKey]);

  // Opens the SAME SpreadsheetMappingPanel used for a fresh upload — reused
  // for an existing "awaiting_mapping" document in Upload History (or a
  // per-zone card row) via its Map Columns action. Dedupes so re-clicking an
  // already-open panel just scrolls to it instead of adding a second one.
  const openMapping = (doc) => {
    const key = `map-${doc._id}`;
    setPendingSpreadsheets(prev => (
      prev.some(p => p.docId === doc._id)
        ? prev
        : [...prev, { docId: doc._id, fileName: doc.original_name, uploadKey: key, fromVault: !!doc.source_vault_file_id }]
    ));
    setScrollToMappingKey(key);
  };

  const handleCreateManualSheet = async () => {
    const title = window.prompt("Name this manual entry sheet (e.g., 'Cash Receipts Jan 2025'):", "Manual Entry Sheet");
    if (!title) return;
    try {
      const { data } = await axios.post(`${API}/documents/manual`, { title }, { withCredentials: true });
      await fetchDocs();
      // Navigate user to Transactions hub so they can start adding line items
      navigate(`/app/transactions?focus_doc=${data.id}&type=Manual%20Entry%20%2F%20Cash%20Transactions`);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not create manual sheet.");
    }
  };

  const handleUpload = async (files, docType) => {
    if (!files || files.length === 0) return;
    setError("");
    for (const file of Array.from(files)) {
      await uploadOneFile(file, docType);
    }
  };

  // Single-file upload — extracted so the retry button can call it directly
  // without re-routing through the file picker. Keeps the original `file`
  // reference in the status tile so retries don't lose the bytes.
  const uploadOneFile = async (file, docType) => {
    const key = `${docType}-${Date.now()}-${file.name}`;
    setUploading(prev => ({ ...prev, [key]: { name: file.name, status: "uploading", progress: 0, file, docType } }));
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("document_type", docType);
      // Phase B — pages are charged against THIS tax year's ledger.
      formData.append("tax_year", String(taxYear));
      const { data: uploadResp } = await axios.post(`${API}/documents/upload`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          const pct = Math.round((evt.loaded / evt.total) * 100);
          setUploading(prev => prev[key]
            ? ({ ...prev, [key]: { ...prev[key], status: "uploading", progress: pct } })
            : prev);
        },
      });
      // Spreadsheet branch: queue the doc for the mapping confirmation panel
      // instead of waiting for OCR. The user finishes mapping → import endpoint
      // creates transactions → docs list refreshes.
      if (uploadResp?.is_spreadsheet || uploadResp?.processing_status === "awaiting_mapping") {
        setUploading(prev => prev[key]
          ? ({ ...prev, [key]: { ...prev[key], status: "awaiting_mapping" } })
          : prev);
        setPendingSpreadsheets(prev => [...prev, { docId: uploadResp.id, fileName: uploadResp.original_name || file.name, uploadKey: key, fromVault: false }]);
        await fetchDocs();
        return;
      }
      setUploading(prev => prev[key]
        ? ({ ...prev, [key]: { ...prev[key], status: "processing", progress: 100 } })
        : prev);
      await fetchDocs();
      setTimeout(() => {
        setUploading(prev => { const n = { ...prev }; delete n[key]; return n; });
      }, 1500);
    } catch (err) {
      const status = err?.response?.status;
      const isAuth = status === 401 || status === 403;
      const detail = err?.response?.data?.detail;
      const message = isAuth
        ? "Session expired, please sign in again."
        : (typeof detail === "string" ? detail : (detail?.message || "Upload failed."));
      // eslint-disable-next-line no-console
      console.warn("[upload] failed", {
        file: file.name,
        docType,
        status,
        isAuth,
        detail,
        url: err?.config?.url,
      });
      setUploading(prev => prev[key]
        ? ({ ...prev, [key]: { ...prev[key], status: isAuth ? "session_expired" : "failed", error: message } })
        : prev);
      setError(message);
    }
  };

  // Spreadsheet panel callbacks
  const handleSpreadsheetImported = (uploadKey) => (result) => {
    setPendingSpreadsheets(prev => prev.filter(p => p.uploadKey !== uploadKey));
    setUploading(prev => { const n = { ...prev }; delete n[uploadKey]; return n; });
    fetchDocs();
    toast.success(
      `Imported ${result.imported_count} transactions${result.skipped_blank_amount ? ` (skipped ${result.skipped_blank_amount} blank rows)` : ""}.`
    );
  };
  const handleSpreadsheetCancel = (uploadKey, docId) => async () => {
    setPendingSpreadsheets(prev => prev.filter(p => p.uploadKey !== uploadKey));
    setUploading(prev => { const n = { ...prev }; delete n[uploadKey]; return n; });
    // Delete the placeholder document so it doesn't sit in the docs list as
    // permanently "awaiting_mapping".
    try {
      await axios.delete(`${API}/documents/${docId}`, { withCredentials: true });
    } catch { /* no-op */ }
    fetchDocs();
  };

  // Fix 1 — actual retry. Re-fires the upload using the stored file reference.
  const retryUpload = (key) => {
    const u = uploading[key];
    if (!u || !u.file || !u.docType) return;
    // Drop the failed tile first so the new tile shows clean "uploading…" state.
    setUploading(prev => { const n = { ...prev }; delete n[key]; return n; });
    setError("");
    uploadOneFile(u.file, u.docType);
  };

  const handleDelete = (docId) => {
    const doc = docs.find(d => d._id === docId);
    if (!doc) return;
    setDeleteTarget({
      kind: "single",
      ids: [docId],
      name: doc.original_name || "this document",
      txnCount: Number(doc.transaction_count || 0),
    });
  };

  const handleBulkDelete = () => {
    const ids = Object.keys(selectedDocs).filter(id => selectedDocs[id]);
    if (!ids.length) return;
    const selected = docs.filter(d => ids.includes(d._id));
    const totalTxns = selected.reduce((sum, d) => sum + Number(d.transaction_count || 0), 0);
    setDeleteTarget({
      kind: "bulk",
      ids,
      name: `${ids.length} documents`,
      txnCount: totalTxns,
    });
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      if (deleteTarget.kind === "single") {
        await axios.delete(`${API}/documents/${deleteTarget.ids[0]}`, { withCredentials: true });
        setDocs(prev => prev.filter(d => d._id !== deleteTarget.ids[0]));
        setSelectedDocs(prev => { const n = { ...prev }; delete n[deleteTarget.ids[0]]; return n; });
      } else {
        await axios.post(`${API}/documents/bulk-delete`, { ids: deleteTarget.ids }, { withCredentials: true });
        setSelectedDocs({});
        await fetchDocs();
      }
      setDeleteTarget(null);
    } catch (err) {
      setError(err.response?.data?.detail || "Delete failed.");
    } finally {
      setDeleting(false);
    }
  };

  const handleRetry = async (docId) => {
    try {
      await axios.post(`${API}/documents/${docId}/retry`);
      await fetchDocs();
    } catch (err) {
      setError(err.response?.data?.detail || "Retry failed.");
    }
  };

  const docsForType = (type) => docs.filter(d => d.document_type === type);

  // Filtered history
  const filteredHistory = useMemo(() => {
    return docs.filter(d => {
      if (historyTypeFilter && d.document_type !== historyTypeFilter) return false;
      if (historyStatusFilter && d.processing_status !== historyStatusFilter) return false;
      if (historySearch && !d.original_name?.toLowerCase().includes(historySearch.toLowerCase())) return false;
      return true;
    });
  }, [docs, historyTypeFilter, historyStatusFilter, historySearch]);

  const historyCounts = useMemo(() => {
    const c = { total: docs.length, extracted: 0, processing: 0, errors: 0, pending: 0 };
    docs.forEach(d => {
      if (d.processing_status === "processed") c.extracted += 1;
      else if (d.processing_status === "processing") c.processing += 1;
      else if (d.processing_status === "error") c.errors += 1;
      else if (d.processing_status === "pending") c.pending += 1;
    });
    return c;
  }, [docs]);

  const selectedCount = Object.values(selectedDocs).filter(Boolean).length;

  return (
    <div className="p-6 lg:p-8 max-w-[1600px] mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>Upload & Extract</h1>
        <p className="text-slate-500 text-sm mt-1">Upload your financial documents. We'll extract and organize everything automatically using AI.</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg flex items-center gap-2" data-testid="upload-error">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
          <button onClick={() => setError("")} className="ml-auto"><X className="w-4 h-4" /></button>
        </div>
      )}

      {Object.values(uploading).length > 0 && (
        <div className="space-y-2" data-testid="upload-status-list">
          {Object.entries(uploading).map(([key, u]) => {
            const status = u.status || "uploading";
            const cfg = {
              uploading:        { bg: "bg-blue-50",   border: "border-blue-200",   text: "text-blue-700",   icon: Loader,      spin: true,  label: `Uploading ${u.progress != null ? u.progress + "%" : ""}` },
              processing:       { bg: "bg-amber-50",  border: "border-amber-200",  text: "text-amber-700",  icon: Loader,      spin: true,  label: "Processing — OCR extracting transactions…" },
              awaiting_mapping: { bg: "bg-blue-50",   border: "border-blue-200",   text: "text-blue-700",   icon: FileText,    spin: false, label: "Spreadsheet ready — confirm column mapping below." },
              session_expired:  { bg: "bg-amber-50",  border: "border-amber-300",  text: "text-amber-800",  icon: AlertCircle, spin: false, label: u.error || "Session expired, please sign in again." },
              failed:           { bg: "bg-red-50",    border: "border-red-200",    text: "text-red-700",    icon: AlertCircle, spin: false, label: u.error || "Failed — please retry." },
            }[status] || { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700", icon: Loader, spin: true, label: "Uploading…" };
            const Icon = cfg.icon;
            return (
              <div key={key}
                data-testid={`upload-status-${status}`}
                className={`${cfg.bg} border ${cfg.border} rounded-lg px-4 py-3 flex items-center gap-3`}>
                <Icon className={`w-4 h-4 ${cfg.text} ${cfg.spin ? "animate-spin" : ""}`} />
                <span className={`${cfg.text} text-sm font-medium truncate flex-1`}>
                  {u.name} — {cfg.label}
                </span>
                {(status === "failed" || status === "session_expired") && (
                  <div className="flex items-center gap-2">
                    {status === "failed" && (
                      <button
                        type="button"
                        onClick={() => retryUpload(key)}
                        className="px-3 py-1 rounded-md bg-white border border-red-300 text-red-700 hover:bg-red-50 text-xs font-semibold transition-all"
                        data-testid={`upload-retry-${key}`}
                      >
                        Retry
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setUploading(prev => { const n = { ...prev }; delete n[key]; return n; })}
                      className={`${cfg.text} hover:opacity-70`}
                      data-testid={`upload-dismiss-${status}`}
                      aria-label="Dismiss"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Spreadsheet Import Pipeline — confirmation panel per pending CSV/XLS/XLSX upload */}
      {pendingSpreadsheets.length > 0 && (
        <div ref={mappingPanelRef} className="space-y-3" data-testid="spreadsheet-mapping-list">
          {pendingSpreadsheets.map((p) => (
            <SpreadsheetMappingPanel
              key={p.docId}
              docId={p.docId}
              fileName={p.fileName}
              fromVault={!!p.fromVault}
              onImported={handleSpreadsheetImported(p.uploadKey)}
              onCancel={handleSpreadsheetCancel(p.uploadKey, p.docId)}
            />
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {uploadZones.map(zone => {
          const zoneDocs = docsForType(zone.type);
          const isDragActive = dragOver === zone.type;
          return (
            <div key={zone.type} className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
              {/* Upload Zone */}
              <div
                className={`relative border-2 border-dashed m-3 rounded-xl p-4 text-center cursor-pointer transition-all duration-200 ${isDragActive ? "border-[#2563EB] bg-blue-50" : zone.borderColor + " " + zone.bgColor}`}
                data-testid={`upload-zone-${zone.type.toLowerCase().replace(/[^a-z]/g, "-")}`}
                onDragOver={zone.manual ? undefined : e => { e.preventDefault(); setDragOver(zone.type); }}
                onDragLeave={zone.manual ? undefined : () => setDragOver(null)}
                onDrop={zone.manual ? undefined : e => { e.preventDefault(); setDragOver(null); handleUpload(e.dataTransfer.files, zone.type); }}
                onClick={() => zone.manual ? handleCreateManualSheet() : fileRefs.current[zone.type]?.click()}
              >
                {!zone.manual && (
                  <input
                    ref={el => fileRefs.current[zone.type] = el}
                    type="file"
                    accept={zone.accept}
                    multiple
                    className="hidden"
                    onChange={e => handleUpload(e.target.files, zone.type)}
                    data-testid={`file-input-${zone.type.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  />
                )}
                <div className="text-3xl mb-2">{zone.icon}</div>
                <p className={`font-bold text-sm mb-1 leading-tight ${zone.iconColor}`} style={{ fontFamily: "Manrope" }}>{zone.type}</p>
                <p className="text-slate-500 text-xs mb-2 leading-snug min-h-[32px]">{zone.desc}</p>
                <div className="flex items-center justify-center gap-1.5 text-slate-400 text-[11px]">
                  {zone.manual ? (
                    <><span>+ Create new sheet</span></>
                  ) : (
                    <><Upload className="w-3 h-3" /><span>Drag & drop or click</span></>
                  )}
                </div>
                {!zone.manual && (
                  <p className="text-slate-300 text-[10px] mt-1 truncate">{zone.accept.split(",").join(" ")}</p>
                )}
              </div>

              {/* File list */}
              {zoneDocs.length > 0 && (
                <div className="px-4 pb-4 space-y-2">
                  {/* Scrollable list — shows ALL uploads for this zone without
                      stretching the card. Cap height keeps the grid uniform
                      so one busy column doesn't push every other column down. */}
                  <div className="max-h-[140px] overflow-y-auto space-y-2 pr-1" data-testid={`zone-files-${zone.type.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                  {zoneDocs.map(doc => {
                    const StatusIcon = statusIcon[doc.processing_status] || Loader;
                    return (
                      <div key={doc._id} className="flex items-center gap-2.5 bg-slate-50 rounded-lg px-3 py-2">
                        <FileText className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-slate-700 text-xs font-medium truncate">{doc.original_name}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <StatusIcon className={`w-3 h-3 ${statusClr[doc.processing_status]} ${doc.processing_status === "processing" ? "animate-spin" : ""}`} />
                            <span className={`text-xs ${statusClr[doc.processing_status]}`}>{doc.processing_status}</span>
                            {doc.transaction_count > 0 && (
                              <span className="text-xs text-slate-400">• {doc.transaction_count} transactions</span>
                            )}
                            {doc.file_storage_status === "missing_legacy" && (
                              <span
                                data-testid={`missing-legacy-badge-${doc._id}`}
                                title="This document was uploaded before cloud storage was enabled. The original PDF is unavailable. Extracted transactions remain intact — please re-upload the source file if you need the original."
                                className="inline-flex items-center gap-1 text-[10px] font-medium bg-amber-50 border border-amber-200 text-amber-700 rounded-full px-2 py-0.5"
                              >
                                <AlertTriangle className="w-2.5 h-2.5" />
                                Original missing — re-upload
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => handleDelete(doc._id)}
                          className="text-slate-300 hover:text-red-500 transition-colors"
                          data-testid={`delete-doc-${doc._id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                        {doc.processing_status === "awaiting_mapping" && doc.file_storage_status !== "missing_legacy" && (
                          <button
                            onClick={() => openMapping(doc)}
                            title="Confirm column mapping for this spreadsheet"
                            data-testid={`map-columns-card-${doc._id}`}
                            className="text-blue-500 hover:text-blue-700 transition-colors flex-shrink-0"
                          >
                            <Table2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                  </div>
                  {zoneDocs.some(d => d.processing_status === "processing") ? (
                    <button onClick={fetchDocs} className="w-full text-xs text-slate-400 hover:text-[#2563EB] flex items-center justify-center gap-1 py-1 transition-colors">
                      <RefreshCw className="w-3 h-3" /> Refresh status
                    </button>
                  ) : null}
                  {/* Ship Gate 3a — Print Report + Archive sit SIDE-BY-SIDE in a single
                      action row to keep the card compact. Both buttons render only when
                      the zone has docs; together they occupy ~36px of vertical space. */}
                  {zoneDocs.length > 0 && (
                    <div className="pt-1.5 border-t border-slate-100 flex items-center gap-2">
                      <button
                        onClick={() => handlePrintCard(zone.type)}
                        disabled={!!printing}
                        data-testid={`print-card-${zone.type.toLowerCase().replace(/[^a-z]/g, "-")}`}
                        className="flex-1 min-w-0 text-[11px] font-semibold text-purple-700 hover:text-purple-900 disabled:text-slate-300 flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg border border-purple-200 bg-purple-50 hover:bg-purple-100 transition-colors"
                        title={`Download a branded PDF report for ${zone.type} — Tax Year ${taxYear}`}
                      >
                        {printing === zone.type ? (
                          <><Loader2 className="w-3 h-3 animate-spin flex-shrink-0" /> <span className="truncate">Preparing…</span></>
                        ) : (
                          <><FileText className="w-3 h-3 flex-shrink-0" /> <span className="truncate">Print (TY {taxYear})</span></>
                        )}
                      </button>
                      <ArchiveButton
                        taxYear={taxYear}
                        category={zone.type}
                        source="/api/reports/card-pdf"
                        sourceParams={{ document_type: zone.type }}
                        titleSuffix={`${zoneDocs.length} doc${zoneDocs.length === 1 ? "" : "s"}`}
                        testId={`upload-card-${zone.type.toLowerCase().replace(/[^a-z]/g, "-")}`}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Upload History */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <h2 className="font-bold text-slate-900 text-base" style={{ fontFamily: "Manrope" }}>Upload History</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              <span className="font-semibold text-slate-700">{historyCounts.total}</span> uploaded •{" "}
              <span className="text-emerald-600 font-semibold">{historyCounts.extracted}</span> extracted •{" "}
              <span className="text-blue-600 font-semibold">{historyCounts.processing}</span> processing •{" "}
              <span className="text-amber-600 font-semibold">{historyCounts.pending}</span> pending •{" "}
              <span className="text-red-600 font-semibold">{historyCounts.errors}</span> error{historyCounts.errors !== 1 ? "s" : ""}
            </p>
          </div>
          <button onClick={fetchDocs} className="text-slate-400 hover:text-[#2563EB] transition-colors" data-testid="refresh-docs-btn">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        {/* Filter bar (always visible) */}
        <div className="flex flex-wrap gap-2 items-center mb-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search filename..."
              value={historySearch}
              onChange={e => setHistorySearch(e.target.value)}
              data-testid="history-search-input"
              disabled={docs.length === 0}
              className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB] disabled:bg-slate-50 disabled:cursor-not-allowed"
            />
          </div>
          <select
            value={historyTypeFilter}
            onChange={e => setHistoryTypeFilter(e.target.value)}
            data-testid="history-type-filter"
            disabled={docs.length === 0}
            className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB] bg-white disabled:bg-slate-50 disabled:cursor-not-allowed"
          >
            <option value="">All Types</option>
            {uploadZones.map(z => <option key={z.type} value={z.type}>{z.type}</option>)}
          </select>
          <select
            value={historyStatusFilter}
            onChange={e => setHistoryStatusFilter(e.target.value)}
            data-testid="history-status-filter"
            disabled={docs.length === 0}
            className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB] bg-white disabled:bg-slate-50 disabled:cursor-not-allowed"
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="processing">Processing</option>
            <option value="processed">Extracted</option>
            <option value="error">Error</option>
          </select>
          {(historySearch || historyTypeFilter || historyStatusFilter) && (
            <button onClick={() => { setHistorySearch(""); setHistoryTypeFilter(""); setHistoryStatusFilter(""); }}
              className="text-slate-400 hover:text-slate-700 text-sm flex items-center gap-1" data-testid="history-clear-filters">
              <X className="w-3.5 h-3.5" /> Clear
            </button>
          )}
          {selectedCount > 0 && (
            <button onClick={handleBulkDelete}
              data-testid="history-bulk-delete"
              className="ml-auto bg-red-500 text-white px-3 py-2 rounded-lg text-xs font-semibold hover:bg-red-600 transition-colors flex items-center gap-1.5">
              <Trash2 className="w-3.5 h-3.5" /> Delete {selectedCount} selected
            </button>
          )}
        </div>

        {docs.length === 0 ? (
          <div className="text-center py-12 text-sm text-slate-400 border-2 border-dashed border-slate-200 rounded-lg">
            No documents uploaded yet. Use the zones above to get started.
          </div>
        ) : filteredHistory.length === 0 ? (
          <p className="text-slate-400 text-sm text-center py-8">No documents match your filters.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-3 py-3 w-10">
                    <input
                      type="checkbox"
                      onChange={e => {
                        const next = {};
                        if (e.target.checked) filteredHistory.forEach(d => { next[d._id] = true; });
                        setSelectedDocs(next);
                      }}
                      checked={filteredHistory.length > 0 && filteredHistory.every(d => selectedDocs[d._id])}
                      data-testid="history-select-all"
                    />
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">File</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Type</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Items</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Uploaded</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredHistory.map(doc => {
                  const StatusIcon = statusIcon[doc.processing_status] || Loader;
                  return (
                    <tr
                      key={doc._id}
                      ref={(el) => { rowRefs.current[doc._id] = el; }}
                      data-testid={`history-row-${doc._id}`}
                      className={`border-b border-slate-50 transition-colors ${highlightDocId === doc._id ? "bg-emerald-50 ring-2 ring-inset ring-emerald-300" : "hover:bg-slate-50"}`}
                    >
                      <td className="px-3 py-3">
                        <input
                          type="checkbox"
                          checked={!!selectedDocs[doc._id]}
                          onChange={e => setSelectedDocs(prev => ({ ...prev, [doc._id]: e.target.checked }))}
                          data-testid={`history-select-${doc._id}`}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-slate-400" />
                          <span className="text-slate-700 font-medium max-w-[280px] truncate" title={doc.original_name}>{doc.original_name}</span>
                          <VaultProvenanceBadge sourceVaultFileId={doc.source_vault_file_id} />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-500 text-xs">{doc.document_type}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <StatusIcon className={`w-3.5 h-3.5 ${statusClr[doc.processing_status]} ${doc.processing_status === "processing" ? "animate-spin" : ""}`} />
                          <span className={`text-xs font-medium ${statusClr[doc.processing_status]}`}>
                            {doc.processing_status === "processed" ? "extracted" : doc.processing_status}
                          </span>
                        </div>
                        {doc.processing_status === "error" && doc.error_message && (
                          <p className="text-[11px] text-red-400 mt-0.5 max-w-[200px] truncate" title={doc.error_message}>{doc.error_message}</p>
                        )}
                        {doc.file_storage_status === "missing_legacy" && (
                          <p
                            data-testid={`missing-legacy-row-${doc._id}`}
                            className="text-[11px] text-amber-600 mt-0.5 flex items-center gap-1"
                            title="This document was uploaded before cloud storage was enabled. Extracted transactions remain intact — please re-upload the source file if you need the original."
                          >
                            <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                            Original missing — please re-upload
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-600 text-sm">{doc.transaction_count || 0}</td>
                      <td className="px-4 py-3 text-slate-400 text-xs">
                        {new Date(doc.upload_date).toLocaleDateString()}{" "}
                        <span className="text-slate-300">{new Date(doc.upload_date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3 justify-end">
                          <button
                            onClick={() => navigate("/app/transactions")}
                            title="Review extracted items in Transactions"
                            data-testid={`history-view-${doc._id}`}
                            className="text-slate-400 hover:text-[#2563EB] transition-colors flex items-center gap-1 text-xs"
                          >
                            <Eye className="w-3.5 h-3.5" /> View
                          </button>
                          {doc.processing_status === "awaiting_mapping" && doc.file_storage_status !== "missing_legacy" && (
                            <button
                              onClick={() => openMapping(doc)}
                              title="Confirm column mapping for this spreadsheet"
                              data-testid={`history-map-columns-${doc._id}`}
                              className="text-blue-500 hover:text-blue-700 transition-colors flex items-center gap-1 text-xs"
                            >
                              <Table2 className="w-3.5 h-3.5" /> Map Columns
                            </button>
                          )}
                          {doc.processing_status === "error" && (
                            <button
                              onClick={() => handleRetry(doc._id)}
                              title="Retry OCR extraction"
                              data-testid={`history-retry-${doc._id}`}
                              className="text-amber-500 hover:text-amber-700 transition-colors flex items-center gap-1 text-xs"
                            >
                              <RefreshCw className="w-3.5 h-3.5" /> Retry
                            </button>
                          )}
                          <button onClick={() => handleDelete(doc._id)} className="text-slate-300 hover:text-red-500 transition-colors" data-testid={`table-delete-${doc._id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── DELETE CONFIRMATION MODAL (single + bulk) ── */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteTarget?.kind === "bulk"
                ? `Delete ${deleteTarget?.ids?.length || 0} documents?`
                : `Delete "${deleteTarget?.name || "this document"}"?`}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-slate-600">
                {deleteTarget?.txnCount > 0 ? (
                  <>
                    <p>
                      This will also permanently remove{" "}
                      <span className="font-semibold text-red-600" data-testid="delete-confirm-txn-count">
                        {deleteTarget.txnCount.toLocaleString()} transaction
                        {deleteTarget.txnCount === 1 ? "" : "s"}
                      </span>{" "}
                      imported from {deleteTarget?.kind === "bulk" ? "these files" : "this file"}.
                    </p>
                    <p>
                      Your Schedule&nbsp;C totals, CACFP allocations, and Dashboard numbers will change accordingly.
                    </p>
                    <p className="text-red-600 font-medium">This cannot be undone.</p>
                  </>
                ) : (
                  <p>
                    {deleteTarget?.kind === "bulk"
                      ? "The selected documents have no extracted transactions. Only the files will be removed."
                      : "This document has no extracted transactions yet. Only the file will be removed."}
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="delete-confirm-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting}
              data-testid="delete-confirm-ok"
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600 text-white"
            >
              {deleting ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Deleting…</>
              ) : (
                deleteTarget?.kind === "bulk"
                  ? `Delete ${deleteTarget?.ids?.length || 0} document${(deleteTarget?.ids?.length || 0) === 1 ? "" : "s"}${deleteTarget?.txnCount > 0 ? ` + ${deleteTarget.txnCount.toLocaleString()} transaction${deleteTarget.txnCount === 1 ? "" : "s"}` : ""}`
                  : `Delete document${deleteTarget?.txnCount > 0 ? ` + ${deleteTarget.txnCount.toLocaleString()} transaction${deleteTarget.txnCount === 1 ? "" : "s"}` : ""}`
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
